import TextLoader from '@components/_elements/TextLoader/index.jsx';
import Diagram from '@src/sld/index.js';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import './index.scoped.scss';
import FormattedAlias from '@components/_shared/Translations/FormattedAlias';

import EnergyProduction from '@components/Home/EnergyProduction';
import SystemMeter from '@components/_shared/SystemMeter';
import PVProduction from '@components/Home/PVProduction';
import SOCChart from '@components/Home/SOCChart';
import TempChart from '@components/Home/TempChart';
import WeatherWidget from '@components/Home/WeatherWidget';
import WindProduction from '@components/Home/WindProduction';
import MainChart from '@components/Home/MainChart';
import { useWsSubscribe } from '@store/actionCreators/mqtt.js';
import storeConnector from '@store/storeConnector';
import { tsDueTimeMode } from '@utils/index_ts';
import { getTimezoneOffset } from './utils';

const MemoEnergyProduction = memo(EnergyProduction);
const MemoPVProduction = memo(PVProduction);
const MemoWindProduction = memo(WindProduction);

const HOURLY_INTERVAL = 5 * 60 * 1000;
const DAILY_INTERVAL = 60 * 60 * 1000;

const Overview = ({ actions, siteMeta, timeMode }) => {
  const [hourlyPV, setHourlyPV] = useState([]);
  const [hourlyWind, setHourlyWind] = useState([]);
  const [dailyChartData, setDailyChartData] = useState([]);
  const [loadingChartsData, setLoadingChartsData] = useState({
    hourly: true,
    daily: true,
  });
  const timerHourly = useRef(null);
  const timerDaily = useRef(null);

  const [siteRt, setSiteRtData] = useState({});

  useWsSubscribe({
    site: {
      fields: [
        'SOC',
        'MaxSOC',
        'MinSOC',
        'MaxAmbT',
        'MinAmbT',
        'AvgAmbT',
        'MaxCellT',
        'MinCellT',
        'AvgCellT',
        'AvgHumidity',
        'PVkW',
        'NumPVOnline',
        'PVInvOnline',
        'WindkW',
        'NumWindOnline',
        'WindTOnline',
        'PVMaxT',
        'PVMinT',
        'PVAvgT',
        'UnitAvgP',
        'UnitMaxP',
        'UnitMinP',
      ],
      cb: (data) => setSiteRtData((prev) => ({ ...prev, ...data })),
    },
  });

  const siteMetaRef = useRef(siteMeta);
  useEffect(() => {
    siteMetaRef.current = siteMeta;
  }, [siteMeta]);

  const getFormatHourlyData = (data) => {
    const tzData = data.map((el) => {
      const dateMoment = tsDueTimeMode({
        timeMode: timeMode,
        lat: siteMeta.GPSLat,
        long: siteMeta.GPSLong,
        ts: el.ts,
        returnMoment: true,
      });
      return {
        value: el.value,
        x: dateMoment.hour(),
        date: dateMoment.format('MM/DD/YYYY'),
      };
    });
    let currentDateMoment;
    currentDateMoment = tsDueTimeMode({
      timeMode: timeMode,
      lat: siteMeta.GPSLat,
      long: siteMeta.GPSLong,
      returnMoment: true,
    });
    const currentHourForChart = currentDateMoment.hour();
    currentDateMoment.set({ hour: currentHourForChart, minute: 0, second: 0 });
    const hours = Array(24)
      .fill(0)
      .map((_el, i) => {
        let hour = currentHourForChart - i;
        if (hour < 0) {
          hour = 24 + hour;
        }
        return {
          hour,
          date: currentDateMoment.clone().add(-i, 'h'),
        };
      });
    return hours
      .map((el) => {
        const valueFromData = tzData.find(
          (elData) =>
            elData.x === el.hour &&
            elData.date === el.date.format('MM/DD/YYYY'),
        );
        return {
          value: valueFromData ? valueFromData.value : 0,
          date: el.date.format('YYYY-MM-DD HH:mm:ss'),
        };
      })
      .reverse();
  };

  const formatWindHourlyData = useMemo(
    () => getFormatHourlyData(hourlyWind),
    [hourlyWind],
  );
  const formatPVHourlyData = useMemo(() => {
    return getFormatHourlyData(hourlyPV);
  }, [hourlyPV]);

  const getFormatDailyData = (data) => {
    if (!dailyChartData || !dailyChartData.length || !siteMeta) {
      return [];
    }
    const dataTz = data.map((d) => {
      const date = tsDueTimeMode({
        // timeMode: timeMode,
        timeMode: 'UTC',
        // prevTimeMode: 'UTC',
        lat: siteMeta.GPSLat,
        long: siteMeta.GPSLong,
        ts: d.date,
        returnMoment: true,
      });
      return {
        ...d,
        date: date.format('YYYY-MM-DD'),
      };
    });
    let currentDateMoment = tsDueTimeMode({
      timeMode: timeMode,
      lat: siteMeta.GPSLat,
      long: siteMeta.GPSLong,
      returnMoment: true,
    });
    const dateArr = Array(7)
      .fill(0)
      .map((_el, i) =>
        currentDateMoment.clone().add(-i, 'd').format('YYYY-MM-DD'),
      )
      .reverse();
    const initArr = dateArr.map((el) => ({
      date: el,
      value: { BESS: 0, PV: 0, Index: 0 },
    }));
    return dataTz.reduce((acc, cv) => {
      const newAcc = [...acc];
      if (dateArr.includes(cv.date)) {
        const index = dateArr.findIndex((d) => d === cv.date);
        const dateData = { ...acc[index] };
        Object.keys(cv.value).forEach((f) => {
          if (Object.keys(dateData.value).includes(f)) {
            dateData.value[f] = +dateData.value[f] + +cv.value[f];
            dateData.value[f] = +dateData.value[f].toFixed(3);
          }
        });
        newAcc[index] = dateData;
      }
      return newAcc;
    }, initArr);
  };

  const formatDailyData = useMemo(
    () => getFormatDailyData(dailyChartData),
    [dailyChartData],
  );

  const setHourlyData = (data) => {
    const dataPV = [];
    const dataWind = [];
    data.forEach((d) => {
      if (d.PVkW !== undefined && d.PVkW !== null) {
        dataPV.push({
          value: +(+d.PVkW).toFixed(3),
          ts: d.time,
        });
      }
      if (d.WindkW !== undefined && d.WindkW !== null) {
        dataWind.push({
          value: +(+d.WindkW).toFixed(3),
          ts: d.time,
        });
      }
    });
    setHourlyPV(dataPV.reverse());
    setHourlyWind(dataWind.reverse());
  };
  const setDailyData = (data) => {
    const dailySumData = data
      .map((entity) => ({
        ...entity,
        BESSkW: +(entity.BESSkW ?? 0),
        PVkW: +(entity.PVkW ?? 0),
        WindkW: +(entity.WindkW ?? 0),
      }))
      .reduce((acc, cur) => {
        const date = cur.time.split('T')[0];
        if (acc[date] === undefined) {
          acc[date] = {
            ...cur,
            count: 1,
          };
        } else {
          acc[date] = {
            BESSkW: cur.BESSkW + acc[date].BESSkW,
            PVkW: cur.PVkW + acc[date].PVkW,
            WindkW: cur.WindkW + acc[date].WindkW,
            count: acc[date].count + 1,
          };
        }

        return acc;
      }, {});

    const dailyChartData = Object.entries(dailySumData).map(
      ([date, entity]) => ({
        date: date,
        value: {
          BESS: entity.BESSkW.toFixed(3),
          PV: entity.PVkW.toFixed(3),
          Index: entity.WindkW.toFixed(3),
        },
      }),
    );

    setDailyChartData(dailyChartData);
  };

  const setInitData = () => {
    if (siteMetaRef.current) {
      const timezoneOffset = getTimezoneOffset({
        siteMeta: siteMetaRef.current,
        timeMode,
      });

      actions.getEnergyProduction('hourly', timezoneOffset).then((res) => {
        if (res) {
          setHourlyData(res);
        }
        setLoadingChartsData((loadingChartsData) => ({
          ...loadingChartsData,
          hourly: false,
        }));
      });
    }
    if (timerHourly.current) {
      clearInterval(timerHourly.current);
    }
    timerHourly.current = setInterval(() => {
      if (siteMetaRef.current) {
        actions
          .getEnergyProduction(
            'hourly',
            getTimezoneOffset({ siteMeta: siteMetaRef.current, timeMode }),
          )
          .then((res) => {
            if (res) {
              setHourlyData(res);
            }
          });
      }
    }, HOURLY_INTERVAL);
    if (siteMetaRef.current) {
      const timezoneOffset = getTimezoneOffset({
        siteMeta: siteMetaRef.current,
        timeMode,
      });

      actions.getEnergyProduction('daily', timezoneOffset).then((res) => {
        if (res) {
          setDailyData(res);
        }
        setLoadingChartsData((loadingChartsData) => ({
          ...loadingChartsData,
          daily: false,
        }));
      });
    }
    if (timerDaily.current) {
      clearInterval(timerDaily.current);
    }
    timerDaily.current = setInterval(() => {
      if (siteMetaRef.current) {
        const currentOffset = getTimezoneOffset({
          siteMeta: siteMetaRef.current,
          timeMode,
        });
        actions.getEnergyProduction('daily', currentOffset).then((res) => {
          if (res) {
            setDailyData(res);
          }
        });
      }
    }, DAILY_INTERVAL);
  };

  useEffect(() => {
    const setInfo = () => {
      if (siteMetaRef.current) {
        setInitData();
      } else {
        setTimeout(() => {
          setInfo();
        }, 500);
      }
    };
    setInfo();

    return () => {
      if (timerHourly.current) {
        clearInterval(timerHourly.current);
      }
      if (timerDaily.current) {
        clearInterval(timerDaily.current);
      }
    };
  }, [timeMode]);

  const isSiteLoading = (field) => {
    return !Object.keys(siteRt).includes(field);
  };

  const windActive = useMemo(
    () => siteMeta && siteMeta.NumWind !== '0' && siteMeta.ui?.Wind_Enable,
    [],
  );
  const pvActive = useMemo(
    () => siteMeta && siteMeta.NumPV !== '0' && siteMeta.ui?.Sun_Enable,
    [],
  );
  const battActive = useMemo(
    () => siteMeta && siteMeta.NumRacks !== '0' && siteMeta.ui?.Battery_Enable,
    [],
  );

  const { dailyEnergy, dailyWind } = useMemo(() => {
    const result = { dailyEnergy: '-', dailyWind: '-' };
    if (dailyChartData) {
      const lastItem = dailyChartData[dailyChartData.length - 1];
      if (lastItem && lastItem.value) {
        if (lastItem.value.PV) {
          result.dailyEnergy = lastItem.value.PV;
        }
        if (lastItem.value.Index) {
          result.dailyWind = lastItem.value.Index;
        }
      }
    }
    return result;
  }, [dailyChartData]);

  const mwEnable = useMemo(() => +siteMeta?.MW_Enable, [siteMeta]);

  return (
    <div className='home-row frow overview-wrapper'>
      <div className='fcol h100'>
        <MainChart
          actions={actions}
          siteMeta={siteMeta}
          battActive={battActive}
          pvActive={pvActive}
          windActive={windActive}
          mwEnable={mwEnable}
        />
        <div className='frow h60'>
          <div className='fcol' style={{ width: '40%' }}>
            <SystemMeter isHome />
          </div>
          <div className='fcol' style={{ width: '60%' }}>
            <SOCChart
              battActive={battActive}
              isSiteLoading={isSiteLoading}
              siteRt={siteRt}
            />
            <TempChart
              battActive={battActive}
              isSiteLoading={isSiteLoading}
              siteRt={siteRt}
            />
          </div>
        </div>
      </div>
      <div className='fcol h100'>
        {pvActive && (
          <div className='cell h15 block-container'>
            <div className='in'>
              <div className='pv-wind-area'>
                <table>
                  <tbody>
                    <tr>
                      <td>
                        <div className='title-line mb-2'>
                          <FormattedAlias id='PV' />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <FormattedAlias id='Output' />
                      </td>
                      <td className='right'>
                        <div className='flex-row-s'>
                          <TextLoader
                            after='kW'
                            loading={isSiteLoading('PVkW')}
                          >
                            {Math.round(Number(siteRt.PVkW))}
                          </TextLoader>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <FormattedAlias id='Daily Energy' />
                      </td>
                      <td className='right'>
                        <div className='flex-row-s'>{dailyEnergy} MWh</div>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <FormattedAlias id='Inverters' />
                      </td>
                      <td className='right'>
                        <div className='flex-row-s'>
                          <TextLoader
                            loading={
                              isSiteLoading('NumPVOnline') &&
                              isSiteLoading('PVInvOnline')
                            }
                          >
                            {siteRt.NumPVOnline}
                          </TextLoader>
                          <span>&nbsp;/&nbsp;{siteMeta.NumPV}</span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <MemoPVProduction
                  data={formatPVHourlyData}
                  loading={loadingChartsData.hourly}
                />
              </div>
            </div>
          </div>
        )}
        {windActive && (
          <div className='cell h15 block-container'>
            <div className='in'>
              <div className='pv-wind-area'>
                <table>
                  <tbody>
                    <tr>
                      <td>
                        <div className='title-line mb-2'>
                          <FormattedAlias id='Index' />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <FormattedAlias id='Output' />
                      </td>
                      <td className='right'>
                        <div className='flex-row-s'>
                          <TextLoader
                            after='kW'
                            loading={isSiteLoading('WindkW')}
                          >
                            {Math.round(Number(siteRt.WindkW))} kW
                          </TextLoader>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <FormattedAlias id='Daily Energy' />
                      </td>
                      <td className='right'>
                        <div className='flex-row-s'>{dailyWind} MWh</div>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <FormattedAlias id='Inverters' />
                      </td>
                      <td className='right'>
                        <div className='flex-row-s'>
                          <TextLoader
                            loading={
                              isSiteLoading('NumWindOnline') &&
                              isSiteLoading('WindTOnline')
                            }
                          >
                            {siteRt.NumWindOnline !== undefined
                              ? siteRt.NumWindOnline
                              : siteRt.WindTOnline}
                          </TextLoader>
                          <span>&nbsp;/&nbsp;{siteMeta.NumWind}</span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <MemoWindProduction
                  data={formatWindHourlyData}
                  loading={loadingChartsData.hourly}
                />
              </div>
            </div>
          </div>
        )}
        <div
          className={`cell ${
            pvActive && windActive ? 'h30' : 'h45'
          } block-container`}
        >
          <MemoEnergyProduction
            battActive={battActive}
            pvActive={pvActive}
            windActive={windActive}
            dailyChartData={dailyChartData}
            formatDailyData={formatDailyData}
            loadingChartsData={loadingChartsData}
          />
        </div>
        {siteMeta.ui.Home_Overview_Weather_Widget_Enable === true ? (
          <div className='cell h30 flex-1'>
            <WeatherWidget
              hideSun={!pvActive}
              hideWind={!windActive}
              hideWeather={siteMeta.ui.WeatherForecast_enable === false}
            />
          </div>
        ) : null}
        {siteMeta.ui.Home_Overview_SLD_Widget_Enable === true ? (
          <div
            className='cell block-container flex-1 sld-container'
            style={{
              diagram: {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              },
            }}
          >
            <Diagram />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default storeConnector(Overview, {
  service: ['timeMode'],
  config: ['siteMeta'],
});
